import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { dpoApi } from '../services/api';
import SEOHead from '../components/SEOHead';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const TIPOS = [
  'Confirmação de tratamento',
  'Acesso aos dados',
  'Correção de dados incompletos ou incorretos',
  'Anonimização / bloqueio / eliminação',
  'Portabilidade dos dados',
  'Revogação de consentimento',
  'Informação sobre compartilhamento',
  'Oposição ao tratamento',
  'Revisão de decisão automatizada',
  'Outro',
];

export default function PrivacidadeSolicitacao() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nome: '', email: '', cpf: '',
    tipo: 'Acesso aos dados',
    descricao: '',
    municipio_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.descricao.length < 10) {
      toast.error('A descrição precisa ter pelo menos 10 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await dpoApi.createTitularPublico({
        nome: form.nome,
        email: form.email,
        cpf: form.cpf,
        tipo: form.tipo,
        descricao: form.descricao,
        municipio_id: form.municipio_id || null,
      });
      setResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao enviar solicitação.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-[var(--background)] pb-20">
        <SEOHead title="Solicitação enviada" canonical="/privacidade/solicitacao" />
        <div className="max-w-2xl mx-auto px-4 py-10">
          <Card className="p-8 text-center border-emerald-200 bg-emerald-50">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h1 className="text-2xl font-semibold text-emerald-900 mb-2">Solicitação recebida!</h1>
            <p className="text-emerald-800 text-sm mb-4">
              Guarde seu número de protocolo:{' '}
              <span className="font-mono text-base block mt-1 bg-white px-3 py-1.5 rounded border border-emerald-300 inline-block">
                {result.protocolo}
              </span>
            </p>
            <p className="text-sm text-emerald-700">
              Prazo legal de resposta: até{' '}
              <strong>
                {result.data_prazo
                  ? new Date(result.data_prazo).toLocaleDateString('pt-BR')
                  : '15 dias'}
              </strong>
              {' '}(Art. 19 LGPD).
            </p>
            <p className="text-xs text-emerald-700 mt-4">
              Uma confirmação foi enviada para o seu e-mail.
            </p>
            <Button className="mt-6" onClick={() => navigate('/')} data-testid="back-home">
              Voltar à página inicial
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead
        title="Solicitação LGPD — Direitos do titular"
        description="Exerça seus direitos previstos na LGPD: acesso, correção, portabilidade, exclusão e mais."
        canonical="/privacidade/solicitacao"
      />
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--primary)] hover:underline mb-3"
            data-testid="btn-voltar"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-600" />
            Solicitação LGPD — Direitos do titular
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Conforme Art. 18 da Lei nº 13.709/2018. Você <strong>não precisa</strong> ter conta na plataforma.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <Card className="p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="solicitacao-form">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome completo *</Label>
                <Input required value={form.nome}
                       onChange={(e) => setForm({ ...form, nome: e.target.value })}
                       data-testid="input-nome" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input required type="email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="input-email" />
              </div>
              <div>
                <Label className="text-xs">CPF (apenas para verificação de identidade) *</Label>
                <Input required value={form.cpf}
                       onChange={(e) => setForm({ ...form, cpf: e.target.value })}
                       placeholder="000.000.000-00"
                       data-testid="input-cpf" />
                <p className="text-xs text-gray-500 mt-1">
                  Seu CPF é armazenado apenas em hash (SHA-256). Não guardamos o número original.
                </p>
              </div>
              <div>
                <Label className="text-xs">Tipo de solicitação *</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="select-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Município relacionado (opcional)</Label>
              <Input value={form.municipio_id}
                     onChange={(e) => setForm({ ...form, municipio_id: e.target.value })}
                     placeholder="Slug ou ID do município, se aplicável" />
            </div>

            <div>
              <Label className="text-xs">Descrição detalhada *</Label>
              <Textarea required rows={6} value={form.descricao}
                        onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                        placeholder="Descreva o que você precisa…"
                        data-testid="textarea-descricao" />
              <p className="text-xs text-gray-500 mt-1">{form.descricao.length}/4000</p>
            </div>

            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
              Ao enviar, declaro estar ciente de que: (i) responderemos em até 15 dias corridos
              (Art. 19 LGPD, prorrogável uma vez por justificativa); (ii) seu CPF será usado
              apenas para verificação de identidade; (iii) registros de auditoria são mantidos
              por até 5 anos por exigência regulatória (Art. 10 Res. CD/ANPD nº 15/2024).
            </div>

            <Button type="submit" disabled={submitting} className="w-full sm:w-auto"
                    data-testid="btn-enviar">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar solicitação'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
